import {
  ActivityIndicator,
  Linking,
  Pressable,
  TouchableOpacity,
  View,
  useWindowDimensions,
} from "react-native";
import Animated, {
  Extrapolation,
  interpolate,
  useAnimatedStyle,
  useDerivedValue,
  useSharedValue,
  withSpring,
  withTiming,
} from "react-native-reanimated";
import { Circle, Path, Svg } from "react-native-svg";
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { fp, hp, spH, spV, wp } from "utils/normalizeV2";
import { useDispatch, useSelector } from "react-redux";

import { ANALYTICS_EVENT_NAMES } from "utils/analytics";
import BottomSheetModal from "components/BottomSheetModal";
import Buttons from "components/Buttons";
import { FlashList } from "@shopify/flash-list";
import ImageView from "../../../src/image";

import ReactPlayer from "components/react-player/src/index";
import VText from "components/VText";
import { getShoppableVideoDetails } from "appRedux/reducers/shoppableSlice";
import { router, useGlobalSearchParams, useRouter } from "expo-router";
import { trackV2 } from "utils/Segment";

const timeInterval = 0.3;

const ShoppableVideo = React.forwardRef(({ source = "homepage" }, ref) => {
  const [loading, setLoading] = useState(false);
  const [selectedVideo, setSelectedVideo] = useState(null);
  const [currentPlayTime, setCurrentPlayTime] = useState(0);
  const [showControls, setShowControls] = useState(false);
  const [playing, setPlaying] = useState(true);
  const [duration, setDuration] = useState(0);
  const [displayedProducts, setDisplayedProducts] = useState([]);

  const userId = useSelector((state) => state.AuthReducer.userId);
  const local = useGlobalSearchParams();
  const gender = local?.gender || "female";

  const flatListRef = useRef();
  const _videoRef = useRef();
  const _bottomSheetRef = useRef();
  const currentTimeShared = useSharedValue(0);

  const { height, width } = useWindowDimensions();

  const dispatch = useDispatch();
  const router = useRouter();

  React.useImperativeHandle(
    ref,
    () => ({
      show,
      hide,
    }),
    [userId]
  );

  const productMap = useMemo(() => {
    const map = new Map();
    selectedVideo?.products?.forEach((product) => {
      map.set(product.id, product);
    });
    return map;
  }, [selectedVideo?.products]);

  const groupedProducts = useMemo(() => {
    if (
      !selectedVideo?.productTimeline ||
      !Array.isArray(selectedVideo?.productTimeline)
    ) {
      return {};
    }

    const sortedTimelineProducts = [...selectedVideo.productTimeline].sort(
      (a, b) => a.startSeconds - b.startSeconds
    );

    return sortedTimelineProducts.reduce((acc, item) => {
      const key = Math.floor(item.startSeconds / timeInterval);
      if (!acc[key]) acc[key] = [];
      acc[key].push(item);
      return acc;
    }, {});
  }, [selectedVideo?.productTimeline, timeInterval]);

  const currentProductGroup = useMemo(() => {
    if (!groupedProducts) {
      return [];
    }
    const currentGroupKey = Math.round(currentPlayTime / 0.3) * 0.3;
    const index = Math.floor(currentGroupKey / timeInterval);
    // Get existing indices from groupedProducts
    const existingIndices = Object.keys(groupedProducts)
      .map(Number)
      .sort((a, b) => a - b);
    // Find the maximum index that is less than or equal to the calculated index
    const nearestIndex = existingIndices.reduce(
      (acc, cur) => (cur <= index ? cur : acc),
      -1
    );
    return groupedProducts[nearestIndex] || [];
  }, [currentPlayTime, groupedProducts]);

  useEffect(() => {
    if (!currentProductGroup.length) {
      return;
    }
    const productsToShow = currentProductGroup
      .map((product) => productMap.get(product.productId))
      .filter(Boolean); // Filter out undefined entries

    setDisplayedProducts(productsToShow);

    // Find the index of the first product in productsToShow within selectedVideo?.products
    const firstProductId = productsToShow?.[0]?.id;
    const firstProductIndex = selectedVideo?.products.findIndex(
      (product) => product.id === firstProductId
    );

    if (firstProductIndex !== -1) {
      setTimeout(() => {
        flatListRef.current?.scrollToIndex({
          index: firstProductIndex,
          animated: true,
          viewOffset: 10,
        });
      }, 500);
    }
  }, [currentProductGroup, productMap, selectedVideo]);

  const show = async (videoId = null) => {
    setSelectedVideo(null);
    _bottomSheetRef?.current?.show();
    currentTimeShared.value = 0;
    if (!videoId) {
      // hide();
      return;
    }
    setLoading(true);
    dispatch(getShoppableVideoDetails(videoId))
      .then((res) => {
        setLoading(false);
        if ([200].includes(res?.payload?.status) && res?.payload?.data) {
          const eventData = {
            video_id: res?.payload?.data?.id,
            video_title: res?.payload?.data?.title,
            source: source,
          };
          trackV2(ANALYTICS_EVENT_NAMES.VIDEO_PLAYED, eventData);
          setSelectedVideo(res?.payload?.data);
        } else {
          alert("Error");
        }
      })
      .catch((err) => {
        setLoading(false);
        console.log("err", err);
        hide();
      });
  };

  const hide = async (onClose = () => {}) => {
    _bottomSheetRef?.current?.hide();
    setSelectedVideo(null);
    setCurrentPlayTime(0);
    currentTimeShared.value = 0;
  };

  const progressBarWidth = useDerivedValue(() => {
    return interpolate(
      currentTimeShared.value,
      [0, duration],
      [0, wp(410)],
      Extrapolation.CLAMP
    );
  }, [duration]);

  const progressBarStyle = useAnimatedStyle(() => {
    return {
      width: progressBarWidth.value,
      height: hp(8),
      backgroundColor: "rgba(149, 149, 149, 1)",
      borderRadius: 6,
    };
  }, [duration]);

  const handleResponderMove = (evt) => {
    setPlaying(false);
    const touchX = evt.nativeEvent.locationX;
    const newTime = (touchX / wp(410)) * duration;
    currentTimeShared.value = newTime;
    _videoRef?.current?.seekTo(newTime, "seconds");
  };

  const handleResponderRelease = (evt) => {
    const touchX = evt.nativeEvent.locationX;
    const newTime = (touchX / wp(410)) * duration;
    currentTimeShared.value = newTime;
    _videoRef?.current?.seekTo(newTime, "seconds");
    setPlaying(true);
  };

  const formatTime = (time) => {
    const minutes = Math.floor(time / 60);
    const seconds = Math.floor(time % 60);
    return `${minutes < 10 ? "0" : ""}${minutes}:${
      seconds < 10 ? "0" : ""
    }${seconds}`;
  };

  const animatedStyle = useAnimatedStyle(() => {
    return {
      opacity: withTiming(showControls ? 1 : 0, { duration: 300 }),
      transform: [
        {
          scale: withSpring(showControls ? 1 : 0),
        },
      ],
    };
  });

  const renderItem = ({ item }) => {
    const isCurrent = displayedProducts.find((o) => o?.id == item?.id);
    const time = selectedVideo?.productTimeline?.find(
      (o) => o?.productId == item?.id
    )?.startSeconds;
    return (
      <View
        style={{
          marginBottom: spV(16),
          padding: isCurrent ? wp(8) : 0,
          borderWidth: isCurrent ? 1 : 0,
          flex: 1,
        }}
      >
        <View
          style={{
            flexDirection: "row",
            alignItems: "center",
            marginBottom: hp(16),
            flex: 1,
          }}
        >
          <ImageView
            alt={item?.productName}
            source={{ uri: item?.imageUrls?.[0] }}
            style={{
              width: wp(75),
              height: hp(100),
            }}
          />
          <View
            style={{
              paddingLeft: wp(12),
              paddingVertical: hp(10),
              width: "100%",
              flex: 1,
            }}
          >
            <VText
              text={item?.brandName}
              style={{
                fontSize: fp(16),
                fontWeight: "700",
              }}
              numberOfLines={1}
            />
            <VText
              text={`${item?.productName?.slice(0, 60)}${
                item?.productName?.length > 60 ? "..." : ""
              }`}
              ellipsizeMode="tail"
              style={{
                fontSize: fp(12),
                fontWeight: "400",
                paddingVertical: 4,
                lineHieght: fp(14),
                flex: 1,
              }}
              numberOfLines={2}
            />
            {time && Number(time) != 10000000 && (
              <VText
                text={formatTime(Math.floor(time))}
                style={{
                  fontSize: fp(12),
                  fontWeight: "400",
                }}
              />
            )}
          </View>
        </View>
        <Buttons
          text="View product"
          isInverse={isCurrent ? false : true}
          noBorder={false}
          containerStyle={{
            borderColor: "rgba(0, 0, 0, 0.16)",
          }}
          onPress={() => {
            const eventData = {
              video_id: selectedVideo?.id,
              video_title: selectedVideo?.title,
              product_id: item?.id,
              product_name: item?.productName,
            };
            trackV2(ANALYTICS_EVENT_NAMES.VIDEO_PRODUCT_CLICKED, eventData);
            router.push(
              `${gender == "men" ? "/men" : ""}/product/${item?.id}/${
                item?.brandName
              }/${item?.productName}`?.replaceAll(" ", "-")
            );
            setPlaying(false);
          }}
        />
      </View>
    );
  };

  return (
    <BottomSheetModal
      MAX_HEIGHT_PERC={1}
      START_PERC={1}
      ref={_bottomSheetRef}
      hideLine
      containerStyle={{
        zIndex: 100,
        backgroundColor: "transparent",
      }}
    >
      <TouchableOpacity
        style={{
          backgroundColor: "transparent",
          width,
          height,
          alignItems: "center",
          justifyContent: "center",
          overflow: "hidden",
          zIndex: 100,
          cursor: "auto",
        }}
        activeOpacity={1}
        onPress={() => _bottomSheetRef?.current?.hide()}
      >
        <TouchableOpacity
          style={{
            width: wp(847),
            height: hp(700),
            backgroundColor: "#fff",
            overflow: "hidden",
            cursor: "auto",
          }}
          activeOpacity={1}
        >
          <TouchableOpacity
            onPress={hide}
            activeOpacity={0.8}
            style={{
              position: "absolute",
              right: 20,
              top: 20,
              zIndex: 100,
            }}
          >
            <Svg
              width="24"
              height="24"
              viewBox="0 0 24 24"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <Circle cx="12" cy="12" r="12" fill="#F7F7F7" />
              <Path
                fillRule="evenodd"
                clipRule="evenodd"
                d="M11.4693 12.0006L7.75684 15.713L8.28717 16.2433L11.9996 12.5309L15.712 16.2433L16.2423 15.713L12.5299 12.0006L16.2423 8.28814L15.712 7.75781L11.9996 11.4702L8.28717 7.75781L7.75684 8.28814L11.4693 12.0006Z"
                fill="#212427"
              />
            </Svg>
          </TouchableOpacity>
          {loading && (
            <ActivityIndicator
              size={"large"}
              style={{
                marginTop: 100,
              }}
            />
          )}
          {selectedVideo?.videoUrl && (
            <View
              style={{
                flexDirection: "row",
              }}
            >
              <Pressable
                onPress={() => {
                  setPlaying((prev) => !prev);
                  setTimeout(() => {
                    setShowControls(true);
                  }, 100);
                  setTimeout(() => {
                    setShowControls(false);
                  }, 600);
                }}
                style={{
                  width: wp(525),
                  height: hp(700),
                }}
              >
                <Animated.View
                  style={[
                    animatedStyle,
                    {
                      position: "absolute",
                      zIndex: 1000,
                      backgroundColor: "rgba(0, 0, 0, 0.8)",
                      height: 70,
                      width: 70,
                      borderRadius: 100,
                      alignSelf: "center",
                      justifyContent: "center",
                      top: "40%",
                      alignItems: "center",
                    },
                  ]}
                >
                  {playing && (
                    <>
                      <Svg
                        width="18"
                        height="22"
                        viewBox="0 0 12 17"
                        fill="none"
                        xmlns="http://www.w3.org/2000/svg"
                      >
                        <Path
                          d="M0 15.2469V1.25311C0 0.433474 0.994271 -0.0463133 1.69238 0.453465L11.5716 7.45036C12.1428 7.85018 12.1428 8.64982 11.5716 9.04964L1.69238 16.0465C0.994271 16.5463 0 16.0665 0 15.2469Z"
                          fill="white"
                        />
                      </Svg>
                    </>
                  )}

                  {!playing && (
                    <Svg
                      width="18"
                      height="21"
                      viewBox="0 0 12 14"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <Path
                        fillRule="evenodd"
                        clipRule="evenodd"
                        d="M4 14H0V0H4V14ZM8 14V0H12V14H8Z"
                        fill="white"
                      />
                    </Svg>
                  )}
                </Animated.View>
                {selectedVideo && (
                  <View
                    style={{
                      width: wp(525),
                      height: hp(700),
                      backgroundColor: "black",
                    }}
                  >
                    <ReactPlayer
                      width={wp(525)}
                      height={hp(700)}
                      playing={playing}
                      url={selectedVideo?.videoUrl}
                      progressInterval={300}
                      onProgress={(data) => {
                        setCurrentPlayTime(data?.playedSeconds);
                        currentTimeShared.value = data?.playedSeconds;
                      }}
                      ref={_videoRef}
                      onDuration={(e) => {
                        setDuration(e);
                      }}
                      loop={true}
                    />
                  </View>
                )}
                <View
                  style={{
                    position: "absolute",
                    bottom: 0,
                    width: wp(525),
                    height: hp(32),
                    backgroundColor: "rgba(33, 36, 39, 0.16)",
                    flexDirection: "row",
                    alignItems: "center",
                    paddingHorizontal: wp(4),
                  }}
                >
                  <Pressable
                    onPress={() => {
                      setPlaying((prev) => !prev);
                    }}
                  >
                    {playing && (
                      <Svg
                        width="17"
                        height="16"
                        viewBox="0 0 17 16"
                        fill="none"
                        xmlns="http://www.w3.org/2000/svg"
                      >
                        <Path
                          d="M5.83333 3.3335C5.47971 3.3335 5.14057 3.47397 4.89052 3.72402C4.64048 3.97407 4.5 4.31321 4.5 4.66683V11.3335C4.5 11.6871 4.64048 12.0263 4.89052 12.2763C5.14057 12.5264 5.47971 12.6668 5.83333 12.6668C6.18696 12.6668 6.52609 12.5264 6.77614 12.2763C7.02619 12.0263 7.16667 11.6871 7.16667 11.3335V4.66683C7.16667 4.31321 7.02619 3.97407 6.77614 3.72402C6.52609 3.47397 6.18696 3.3335 5.83333 3.3335ZM11.1667 3.3335C10.813 3.3335 10.4739 3.47397 10.2239 3.72402C9.97381 3.97407 9.83333 4.31321 9.83333 4.66683V11.3335C9.83333 11.6871 9.97381 12.0263 10.2239 12.2763C10.4739 12.5264 10.813 12.6668 11.1667 12.6668C11.5203 12.6668 11.8594 12.5264 12.1095 12.2763C12.3595 12.0263 12.5 11.6871 12.5 11.3335V4.66683C12.5 4.31321 12.3595 3.97407 12.1095 3.72402C11.8594 3.47397 11.5203 3.3335 11.1667 3.3335Z"
                          fill="white"
                        />
                      </Svg>
                    )}
                    {!playing && (
                      <Svg
                        width="17"
                        height="16"
                        viewBox="0 0 17 16"
                        fill="none"
                        xmlns="http://www.w3.org/2000/svg"
                      >
                        <Path
                          d="M4.5 12.9998C4.317 12.9998 4.135 12.9498 3.974 12.8508C3.82918 12.761 3.70965 12.6357 3.62671 12.4868C3.54378 12.3378 3.50016 12.1703 3.5 11.9998V3.99982C3.5 3.65282 3.68 3.33082 3.974 3.14882C4.11914 3.05958 4.2846 3.00872 4.45481 3.00103C4.62502 2.99333 4.79439 3.02905 4.947 3.10482L12.947 7.10482C13.1131 7.18802 13.2528 7.3158 13.3504 7.47387C13.448 7.63194 13.4997 7.81405 13.4997 7.99982C13.4997 8.1856 13.448 8.36771 13.3504 8.52577C13.2528 8.68384 13.1131 8.81162 12.947 8.89482L4.947 12.8948C4.807 12.9648 4.652 12.9998 4.5 12.9998Z"
                          fill="white"
                        />
                      </Svg>
                    )}
                  </Pressable>

                  <VText
                    text={formatTime(currentPlayTime)}
                    style={{
                      fontSize: fp(12),
                      fontWeight: "600",
                      paddingHorizontal: wp(8),
                      color: "white",
                    }}
                  />

                  <View
                    style={{
                      width: wp(410),
                      height: hp(8),
                      backgroundColor: "rgba(255, 255, 255, 1)",
                      borderRadius: 6,
                    }}
                    onStartShouldSetResponder={() => {
                      setPlaying(false);
                      return true;
                    }}
                    onResponderMove={handleResponderMove} // Handle move to update progress
                    onResponderRelease={handleResponderRelease} // Also update on release
                  >
                    <View
                      style={{
                        backgroundColor: "rgba(255, 255, 255, 1)",
                        height: hp(8),
                        borderRadius: 6,
                      }}
                    >
                      <Animated.View style={progressBarStyle} />
                    </View>
                  </View>

                  <VText
                    text={formatTime(duration)}
                    style={{
                      fontSize: fp(12),
                      fontWeight: "600",
                      paddingLeft: spH(8),
                      color: "white",
                    }}
                  />
                </View>
              </Pressable>
              <View
                style={{
                  width: wp(322),
                  height: hp(700),
                  padding: wp(20),
                  paddingBottom: 0,
                }}
              >
                <VText
                  text={selectedVideo?.title}
                  style={{
                    fontSize: fp(20),
                    fontWeight: "700",
                  }}
                />
                <VText
                  text={`By ${selectedVideo?.recommendedBy}`}
                  style={{
                    fontSize: fp(12),
                    fontWeight: "700",
                    marginTop: spV(12),
                  }}
                />
                <FlashList
                  contentContainerStyle={{
                    paddingTop: hp(20),
                  }}
                  ListEmptyComponent={() => (
                    <VText
                      style={{
                        textAlign: "center",
                        paddingTop: hp(200),
                      }}
                      text={"No Products Attached"}
                    />
                  )}
                  extraData={displayedProducts}
                  showsVerticalScrollIndicator={false}
                  ref={flatListRef}
                  data={selectedVideo?.products || []}
                  estimatedItemSize={300}
                  keyExtractor={(item, index) => index.toString()}
                  renderItem={renderItem}
                />
              </View>
            </View>
          )}
        </TouchableOpacity>
      </TouchableOpacity>
    </BottomSheetModal>
  );
});

ShoppableVideo.displayName = "ShoppableVideo";

export default ShoppableVideo;
